import { mirrorApiFetch, uploadMirrorImage } from './ai.js';

const META_PREFIX = 'mirror_sync_meta_v1:';
const IMAGE_DATA_PATTERN = /^data:image\/(?:jpeg|png|webp|gif);base64,/i;

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export function hasSyncableData(state) {
  const collections = state?.collections || [];
  const hasCustomSpace = collections.length > 1 || (collections[0] && collections[0].name !== 'My Space');
  const poses = Object.values(state?.mirror?.poseSlots || {}).some(slot => slot?.imageData || slot?.mediaId);
  return !!(hasCustomSpace || state?.locations?.length || state?.mirror?.outfits?.length || state?.mirror?.ootdHistory?.length || poses);
}

export function readSyncMeta(userId) {
  try {
    return JSON.parse(localStorage.getItem(`${META_PREFIX}${userId}`) || 'null');
  } catch {
    return null;
  }
}

export function writeSyncMeta(userId, meta) {
  localStorage.setItem(`${META_PREFIX}${userId}`, JSON.stringify(meta));
  return meta;
}

export async function snapshotFingerprint(snapshot) {
  const text = JSON.stringify(snapshot || {});
  if (!globalThis.crypto?.subtle) return `${text.length}:${text.slice(0, 64)}`;
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
}

export async function getMirrorSnapshot({ signal } = {}) {
  return mirrorApiFetch('/api/mirror/snapshot', { signal });
}

export async function putMirrorSnapshot(snapshot, baseRevision, { signal } = {}) {
  return mirrorApiFetch('/api/mirror/snapshot', {
    method: 'PUT',
    body: JSON.stringify({ snapshot, baseRevision }),
    signal,
  });
}

function removeUnsafeLegacyFields(value) {
  if (Array.isArray(value)) {
    value.forEach(removeUnsafeLegacyFields);
    return value;
  }
  if (!value || typeof value !== 'object') return value;
  Object.keys(value).forEach(key => {
    if (/^(?:serverImageUrl|tagServerImageUrl|url|path)$/i.test(key) && /upload/i.test(String(value[key] || ''))) {
      delete value[key];
      return;
    }
    if (/(?:imageData|photoData)$/i.test(key)) value[key] = '';
    else removeUnsafeLegacyFields(value[key]);
  });
  return value;
}

export async function preparePrivateSnapshot(inputState, { onProgress, signal } = {}) {
  const state = clone(inputState);
  const uploadCache = new Map();
  let completed = 0;
  let discovered = 0;

  const protect = async (target, dataKey, idKey, hashKey, kind) => {
    if (!target || typeof target !== 'object') return;
    if (signal?.aborted) {
      const error = new Error('Sync stopped. Your changes remain on this device.');
      error.name = 'AbortError';
      throw error;
    }
    const imageData = typeof target[dataKey] === 'string' ? target[dataKey] : '';
    if (IMAGE_DATA_PATTERN.test(imageData)) {
      discovered += 1;
      onProgress?.({ phase: 'media', completed, total: discovered });
      let upload = uploadCache.get(imageData);
      if (!upload) {
        upload = await uploadMirrorImage(imageData, kind, { signal });
        uploadCache.set(imageData, upload);
      }
      target[idKey] = upload.mediaId;
      target[hashKey] = upload.sha256 || '';
      completed += 1;
      onProgress?.({ phase: 'media', completed, total: Math.max(discovered, completed) });
    }
    target[dataKey] = '';
  };

  for (const location of state.locations || []) {
    await protect(location, 'imageData', 'imageMediaId', 'imageSha256', 'zone');
    for (const zone of location.zones || []) {
      await protect(zone, 'imageData', 'imageMediaId', 'imageSha256', 'zone');
      zone.rowMediaIds = Array.isArray(zone.rowMediaIds) ? zone.rowMediaIds : [];
      zone.rowSha256 = Array.isArray(zone.rowSha256) ? zone.rowSha256 : [];
      for (let index = 0; index < (zone.rowImages || []).length; index += 1) {
        const holder = {
          imageData: zone.rowImages[index],
          mediaId: zone.rowMediaIds[index] || '',
          sha256: zone.rowSha256[index] || '',
        };
        await protect(holder, 'imageData', 'mediaId', 'sha256', 'zone');
        zone.rowImages[index] = '';
        zone.rowMediaIds[index] = holder.mediaId || '';
        zone.rowSha256[index] = holder.sha256 || '';
      }
      for (const item of zone.items || []) {
        await protect(item, 'imageData', 'imageMediaId', 'imageSha256', 'item');
        await protect(item, 'backImageData', 'backImageMediaId', 'backImageSha256', 'item');
        await protect(item, 'tagImageData', 'tagImageMediaId', 'tagImageSha256', 'care-label');
        item.mediaStatus = item.imageMediaId ? 'private' : item.mediaStatus || 'empty';
        delete item.serverImageUrl;
        delete item.tagServerImageUrl;
      }
    }
  }

  const mirror = state.mirror || {};
  mirror.poseSlots = mirror.poseSlots || {};
  mirror.views = mirror.views || {};
  mirror.viewMediaIds = mirror.viewMediaIds || {};
  for (const poseId of ['front', 'quarter', 'side', 'back', 'move']) {
    const slot = mirror.poseSlots[poseId] || {
      imageData: mirror.views[poseId] || '',
      mediaId: mirror.viewMediaIds[poseId] || '',
      sha256: '',
    };
    await protect(slot, 'imageData', 'mediaId', 'sha256', 'pose');
    slot.syncStatus = slot.mediaId ? 'private' : 'empty';
    mirror.poseSlots[poseId] = slot;
    mirror.views[poseId] = '';
    mirror.viewMediaIds[poseId] = slot.mediaId || '';
  }
  const activeSlot = mirror.poseSlots[mirror.activePoseId || mirror.activeView || 'front'] || mirror.poseSlots.front;
  mirror.subjectImageData = '';
  mirror.subjectMediaId = activeSlot?.mediaId || mirror.subjectMediaId || '';

  for (const outfit of mirror.outfits || []) {
    await protect(outfit, 'snapshotImageData', 'snapshotMediaId', 'snapshotSha256', 'render');
    outfit.resultMediaId = outfit.resultMediaId || outfit.snapshotMediaId || '';
    await protect(outfit, 'subjectImageData', 'subjectMediaId', 'subjectSha256', 'pose');
    for (const item of outfit.items || []) {
      await protect(item, 'imageData', 'imageMediaId', 'imageSha256', 'garment');
    }
  }
  for (const entry of mirror.ootdHistory || []) {
    await protect(entry, 'photoData', 'photoMediaId', 'photoSha256', 'try-on');
  }

  const itemMedia = new Map();
  for (const location of state.locations || []) {
    for (const zone of location.zones || []) {
      for (const item of zone.items || []) itemMedia.set(item.id, item.imageMediaId || '');
    }
  }
  for (const entry of state.activity?.auditLog || []) {
    entry.imageMediaId = entry.imageMediaId || itemMedia.get(entry.itemId) || '';
    entry.imageData = '';
  }
  for (const key of ['lastAdded', 'lastMoved', 'lastRemoved']) {
    const entry = state.activity?.[key];
    if (entry) {
      entry.imageMediaId = entry.imageMediaId || itemMedia.get(entry.itemId) || '';
      entry.imageData = '';
    }
  }

  removeUnsafeLegacyFields(state);
  return { snapshot: state, uploaded: completed };
}
